import { forwardRef, useEffect, useRef, useState } from 'react'
import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export function TableContainer({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'relative w-full overflow-auto rounded-md border border-slate-200 bg-white',
        className,
      )}
      {...rest}
    />
  )
}

export function Table({ className, ...rest }: HTMLAttributes<HTMLTableElement>) {
  return <table className={cn('w-full caption-bottom text-sm', className)} {...rest} />
}

export function TableHead({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={cn(
        'sticky top-0 z-10 bg-slate-50 text-slate-600',
        // borde inferior visible incluso con sticky (los borders de th colapsan en sticky)
        'shadow-[inset_0_-1px_0_0] shadow-slate-200',
        className,
      )}
      {...rest}
    />
  )
}

export function TableBody({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn('divide-y divide-slate-100', className)} {...rest} />
}

export function TableRow({ className, ...rest }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn('hover:bg-slate-50 data-[state=selected]:bg-slate-100', className)}
      {...rest}
    />
  )
}

type SortDir = 'asc' | 'desc' | null

interface TableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean
  sortDir?: SortDir
  onSort?: () => void
}

export function TableHeaderCell({
  className,
  children,
  sortable,
  sortDir = null,
  onSort,
  ...rest
}: TableHeaderCellProps) {
  return (
    <th
      scope="col"
      className={cn(
        'whitespace-nowrap px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide',
        sortable && 'cursor-pointer select-none',
        className,
      )}
      onClick={sortable ? onSort : undefined}
      aria-sort={sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : undefined}
      {...rest}
    >
      <span className="inline-flex items-center gap-1.5">
        {children}
        {sortable &&
          (sortDir === 'asc' ? (
            <ArrowUp className="h-3 w-3" aria-hidden />
          ) : sortDir === 'desc' ? (
            <ArrowDown className="h-3 w-3" aria-hidden />
          ) : (
            <ArrowUpDown className="h-3 w-3 text-slate-400" aria-hidden />
          ))}
      </span>
    </th>
  )
}

export function TableCell({ className, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('whitespace-nowrap px-3 py-2 text-slate-800', className)} {...rest} />
}

/**
 * Empty-state de tabla. Es el CONTENIDO interno de una celda: SIEMPRE debe ir dentro de
 * `<tr><td colSpan={N}>` (así lo usan ~10 tablas). #17: los 3 call-sites que lo metían
 * directo en <tbody> (Organizations/Operativos/Expediente) fueron envueltos en `<tr><td>`.
 */
export function TableEmpty({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 p-8 text-center text-sm text-slate-500">
      {children}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Inline editable cell                                                         */
/* -------------------------------------------------------------------------- */

interface InlineEditCellProps {
  value: string
  onCommit: (next: string) => void | Promise<void>
  onCancel?: () => void
  placeholder?: string
  type?: 'text' | 'email' | 'number'
  disabled?: boolean
  className?: string
  'data-testid'?: string
  formatDisplay?: (v: string) => string
}

/**
 * Celda editable inline:
 * - display mode: muestra valor con estilo discreto y focus:outline para indicar que es interactiva.
 * - edit mode: input que se activa con click; Enter commit, Escape cancel, blur commit (optimistic).
 */
export const InlineEditCell = forwardRef<HTMLDivElement, InlineEditCellProps>(
  function InlineEditCell(
    {
      value,
      onCommit,
      onCancel,
      placeholder,
      type = 'text',
      disabled,
      className,
      formatDisplay,
      ...rest
    },
    ref,
  ) {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState(value)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (!editing) setDraft(value)
    }, [value, editing])

    useEffect(() => {
      if (editing) {
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }, [editing])

    if (disabled) {
      return (
        <div ref={ref} className={cn('text-slate-500', className)} {...rest}>
          {formatDisplay ? formatDisplay(value) : value}
        </div>
      )
    }

    if (!editing) {
      return (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={cn(
            // Pilar 1 affordance (Wipxap Standards 2026-05-18):
            // border-dashed transparent siempre presente → no layout shift al hover;
            // hover:border-slate-300 + hover:bg-slate-50 → señaliza editable;
            // cursor-text → user descubre "click para editar" sin instrucciones.
            'group flex w-full cursor-text items-center justify-between gap-1 rounded border border-dashed border-transparent px-1 py-1 text-left hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-fitlab-blue)]',
            className,
          )}
          aria-label={`Editar celda (valor actual: ${value || 'vacío'})`}
        >
          <span className={cn(!value && 'text-slate-400')}>
            {value ? (formatDisplay ? formatDisplay(value) : value) : placeholder || '—'}
          </span>
        </button>
      )
    }

    const commit = async () => {
      if (draft === value) {
        setEditing(false)
        return
      }
      await onCommit(draft)
      setEditing(false)
    }

    const cancel = () => {
      setDraft(value)
      setEditing(false)
      onCancel?.()
    }

    return (
      <input
        ref={inputRef}
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void commit()
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            cancel()
          }
        }}
        placeholder={placeholder}
        className={cn(
          'h-8 w-full rounded border border-[color:var(--color-fitlab-blue)] bg-white px-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[color:var(--color-fitlab-blue)]',
          className,
        )}
      />
    )
  },
)
